import { useCallback, useMemo, useState } from 'react';
import {
  columnVisibilityKey,
  columnWidthsKey,
  createUserPreferenceStore,
  savedViewsKey,
} from '../../lib/preferences';
import { useCurrentSession } from '../../stores/session';
import type { Column, SavedView } from './types';

// Column widths, column visibility and saved views — "persisted per user per
// screen" (06-qol §3).
//
// Users arrive in S3/S4, so today this is browser storage namespaced by the
// viewer's id. It all goes through `PreferenceStore`, so when S4 lands an
// API-backed store drops in behind the same interface and nothing here changes.

const DEFAULT_WIDTH = 180;
const IDENTITY_WIDTH = 240;
const MIN_WIDTH = 56;
const NONE: readonly string[] = [];

export interface TablePreferences {
  readonly widths: Readonly<Record<string, number>>;
  readonly hiddenColumns: ReadonlySet<string>;
  readonly savedViews: readonly SavedView[];
  setWidth(columnId: string, width: number): void;
  toggleColumn(columnId: string): void;
  /** A saved view choosing the columns (S16 F8.14). */
  setHiddenColumns(columnIds: readonly string[]): void;
  resetColumns(): void;
  saveView(view: SavedView): void;
  deleteView(viewId: string): void;
}

export function useTablePreferences<TRow>(
  screenId: string,
  columns: readonly Column<TRow>[],
  /** Columns a screen starts with hidden until the viewer shows them (S16 Q7). */
  defaultHidden: readonly string[] = NONE,
): TablePreferences {
  const userId = useCurrentSession()?.id ?? 'anonymous';
  const store = useMemo(() => createUserPreferenceStore(userId), [userId]);

  const [widths, setWidths] = useState<Record<string, number>>(() =>
    store.get<Record<string, number>>(columnWidthsKey(screenId), {}),
  );
  const [hidden, setHidden] = useState<readonly string[]>(() =>
    store.get<readonly string[]>(columnVisibilityKey(screenId), defaultHidden),
  );
  const [savedViews, setSavedViews] = useState<readonly SavedView[]>(() =>
    store.get<readonly SavedView[]>(savedViewsKey(screenId), []),
  );

  const setWidth = useCallback(
    (columnId: string, width: number) => {
      setWidths((current) => {
        const next = { ...current, [columnId]: Math.max(MIN_WIDTH, Math.round(width)) };
        store.set(columnWidthsKey(screenId), next);
        return next;
      });
    },
    [store, screenId],
  );

  const toggleColumn = useCallback(
    (columnId: string) => {
      // The identity column is how a row is recognised; hiding it would leave a
      // grid of attributes belonging to nobody.
      if (columns.find((column) => column.id === columnId)?.isIdentity === true) return;
      setHidden((current) => {
        const next = current.includes(columnId)
          ? current.filter((id) => id !== columnId)
          : [...current, columnId];
        store.set(columnVisibilityKey(screenId), next);
        return next;
      });
    },
    [store, screenId, columns],
  );

  const setHiddenColumns = useCallback(
    (columnIds: readonly string[]) => {
      const next = columnIds.filter((id) => columns.some((column) => column.id === id && column.isIdentity !== true));
      setHidden(next);
      store.set(columnVisibilityKey(screenId), next);
    },
    [store, screenId, columns],
  );

  const resetColumns = useCallback(() => {
    setHidden(defaultHidden);
    setWidths({});
    store.remove(columnVisibilityKey(screenId));
    store.remove(columnWidthsKey(screenId));
  }, [store, screenId, defaultHidden]);

  const saveView = useCallback(
    (view: SavedView) => {
      setSavedViews((current) => {
        const next = [...current.filter((v) => v.id !== view.id), view];
        store.set(savedViewsKey(screenId), next);
        return next;
      });
    },
    [store, screenId],
  );

  const deleteView = useCallback(
    (viewId: string) => {
      setSavedViews((current) => {
        const next = current.filter((v) => v.id !== viewId);
        store.set(savedViewsKey(screenId), next);
        return next;
      });
    },
    [store, screenId],
  );

  return {
    widths,
    hiddenColumns: useMemo(() => new Set(hidden), [hidden]),
    savedViews,
    setWidth,
    toggleColumn,
    setHiddenColumns,
    resetColumns,
    saveView,
    deleteView,
  };
}

export function widthOf<TRow>(
  column: Column<TRow>,
  widths: Readonly<Record<string, number>>,
): number {
  return widths[column.id] ?? column.defaultWidth ?? (column.isIdentity ? IDENTITY_WIDTH : DEFAULT_WIDTH);
}

export { MIN_WIDTH };
